import { useState, useMemo, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Label from '../../components/Label'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import Autocomplete from '@mui/material/Autocomplete'
import Prompt from '../../components/Prompt'
import useContentful from '../../contexts/useContentful'
import useQualtricsSurvey from '../../hooks/useQualtricsSurvey'
import { resetState } from '../../redux/slices/qualtricsSurveySlice'
import { changeLanguage } from '../../redux/slices/languageSlice'
import { setHasReferrerID } from '../../redux/slices/globalSlice'
import { find } from 'lodash'
import { ZIP_CODES } from '../../data/zipCodes'
import { ReactComponent as Bee } from '../../icons/Bee.svg'
import { SURVEY_ID } from '../../data/surveyID'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
// import useRecaptcha from "../../hooks/useRecaptcha";
// import useIpify from '../../hooks/useIpify'

const getQueryParam = param => {
  const queryString = window.location.search
  const urlParams = new URLSearchParams(queryString)
  return urlParams.get(param)
}

const redirect = window.sessionStorage?.getItem('redirect') || getQueryParam('redirect')
const lang = window.sessionStorage?.getItem('lang') || getQueryParam('lang')
const isRandomSample = window.sessionStorage?.getItem('isRandomSample') || getQueryParam('rs')
const referrerID = window.sessionStorage?.getItem('referrerID') || getQueryParam('id')

try {
  if (redirect) window.sessionStorage?.setItem('redirect', redirect)
  if (lang) window.sessionStorage?.setItem('lang', lang)
  if (isRandomSample) window.sessionStorage?.setItem('isRandomSample', isRandomSample)
  if (referrerID) window.sessionStorage?.setItem('referrerID', referrerID)
} catch (e) {
  console.error('error: ', e)
}

const JoinConversationSection = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isSessionStarting = useRef(false)
  const selectedLanguage = useSelector(({ language }) => language.value)
  const qualtricsQuestions = useSelector(({ qualtricsSurvey }) => qualtricsSurvey.qualtricsQuestions)
  const responses = useSelector(({ qualtricsSurvey }) => qualtricsSurvey.responses)
  const {
    checkboxLabel,
    beginSurveyButton,
    pageTitle,
    title,
    zipCode,
    zipcodeHelperText,
    howResultsAreUsed,
    errorText,
    surveyEnded,
  } = useContentful()
  const { 
    createSurveySession,
    submitMultipleChoiceAnswer, 
    advanceSurvey,
    hasSessionError,
    qualtricsLoading,
  } = useQualtricsSurvey()
  const attemptsCt = useRef(0)
  const [consent, setConsent] = useState(false)
  const [surveyType, setSurveyType] = useState(-1)
  const [isLoading, setIsLoading] = useState(false)
  const [tryAgainError, setTryAgainError] = useState(false)
  const [redirectReady, setRedirectReady] = useState(false)
  const isDisabled =
    !responses.QID58.choiceId ||
    !consent ||
    surveyType === -1
  // const { executeRecaptcha } = useRecaptcha();
  // const { fetchIp } = useIpify()

  const onBeginSurvey = async overrideSurveyID => {
    setIsLoading(true)
    
    // const response = await executeRecaptcha("submitZipCode")
    // const ip = await fetchIp()
    
    advanceSurvey({
      // score: response?.score,
      // ip: ip,
      nextIndex: 2,
      cb: error => {
        setIsLoading(false)
        if (!error) {
          setTryAgainError(false)
          navigate(`/imagine/${overrideSurveyID || surveyType}`)
        } else {
          setTryAgainError(true)
        }
      },
    })
  }

  const [
    zipCodeOptions,
  ] = useMemo(() => {
    return [
      // ZIP_CODES,
      find(qualtricsQuestions, { questionId: 'QID58' })?.choices || [],
    ]
  }, [
    qualtricsQuestions,
  ])

  useEffect(() => {
    if (lang && (lang === 'es') && lang !== selectedLanguage) {
      dispatch(changeLanguage(lang))
    }
    if (referrerID?.length) {
      dispatch(setHasReferrerID(true))
    }
    if (!isSessionStarting.current) {
      dispatch(resetState())
      isSessionStarting.current = true
      createSurveySession({
        cb: () => {
          isSessionStarting.current = false
          if (redirect) setRedirectReady(true)
        }, 
        lang, 
        isRandomSample: !!isRandomSample,
        id: referrerID,
      })
    }
  }, [])

  // useEffect(() => {
  //   if (hasSessionError && attemptsCt.current < 3) {
  //     attemptsCt.current += 1
  //     if (!isSessionStarting.current) {
  //       dispatch(resetState())
  //       isSessionStarting.current = true
  //       createSurveySession({
  //         cb: () => {
  //           isSessionStarting.current = false
  //         }, 
  //         lang, 
  //         isRandomSample: !!isRandomSample,
  //         id: referrerID,
  //       })
  //     }
  //   }
  // }, [hasSessionError])

  useEffect(() => {
    if (redirectReady && redirect) {
      onBeginSurvey(SURVEY_ID[redirect] || SURVEY_ID.URBAN)
    }
  }, [redirectReady])

  if (redirect || redirectReady) {
    return (
      <CircularProgress 
        size={50}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          m: 'auto',
        }}
      />
    )
  }

  return (
    <Stack
      display="grid"
      gap={4}
    >
      <Stack
        display="grid"
        justifyContent="center"
      >
        <Box
          justifySelf="flex-end"
          position="relative"
          right="30px"
          height={{
            xs: 60,
            sm: 'initial',
          }}
          sx={{
            'svg': {
              height: 'inherit',
            },
          }}
        >
          <Bee />
        </Box>
        <Typography
          variant="h2"
          textAlign="center"
          sx={{
            maxWidth: 870,
            justifySelf: 'center',
          }}
        >
          {pageTitle}
        </Typography>
      </Stack>
      <Paper
        elevation={0}
        sx={{
          bgcolor: 'primary.lightest',
          maxWidth: 640,
          width: '100%',
          justifySelf: 'center',
          borderRadius: '20px',
        }}
      >
        <Stack
          p={2}
          textAlign="center"
        >
          <Typography
            variant="body"
            color="text.neutralDark"
          >
            {documentToReactComponents(surveyEnded)}
          </Typography>
        </Stack>
      </Paper>
      <Stack
        display="grid"
        justifySelf="center"
        gap={4}
        maxWidth="475px"
      >
        <Paper
          className="drawn"
          sx={{
            width: '100%',
            justifySelf: 'center',
          }}
        >
          <Stack
            p={{
              xs: 2,
              sm: 4,
            }}
            gap={4}
          >
            <Typography
              variant="h4"
              textAlign="center"
            >
              {title}
            </Typography>
            <Stack
              gap={1}
            >
              <Label>
                {zipCode}
              </Label>
              <Stack
                gap={.5}
              >
                <Autocomplete
                  autoHighlight
                  autoSelect
                  value={zipCodeOptions?.length ? responses.QID58 : ''}
                  options={zipCodeOptions}
                  getOptionLabel={option => option && option.display ? option.display : ''}
                  renderInput={params => <TextField {...params} />}
                  onChange={(e, newValue) => {
                    if (newValue && newValue.display) {
                      setSurveyType(find(ZIP_CODES, { zip_code: newValue.display })?.suggested_survey)
                      submitMultipleChoiceAnswer('QID58', newValue?.choiceId, newValue)
                    } else {
                      setSurveyType(null)
                      submitMultipleChoiceAnswer('QID58', null, {})
                    }
                  }}
                />
                <Typography
                  variant="label"
                  color="text.neutralLight"
                >
                  {zipcodeHelperText}
                </Typography>
              </Stack>
            </Stack>
            <FormControl 
              component="fieldset"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Checkbox
                checked={consent}
                onChange={() => setConsent(!consent)}
              />
              <Label>
                {checkboxLabel}
              </Label>
            </FormControl>
            <Stack
              display="grid"
              justifyContent="center"
            >
              <Button
                sx={{
                  position: 'relative',
                }}
                disabled={isDisabled || (isLoading || qualtricsLoading)}
                onClick={() => onBeginSurvey()}
              >
                <span
                  style={{
                    opacity: (isLoading || qualtricsLoading) ? 0 : 1,
                  }}
                >
                  {beginSurveyButton}
                </span>
                {(isLoading || qualtricsLoading) && (
                  <CircularProgress 
                    size={25}
                    sx={{
                      position: 'absolute',
                    }}
                  />
                )}
              </Button>
              {tryAgainError && (
                <Typography
                  variant="body1"
                  color="error"
                  mt={2}
                  textAlign="center"
                >
                  {errorText}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
        <Typography
          variant="label"
          textAlign="center"
        >
          {howResultsAreUsed}
        </Typography>
      </Stack>
      <Prompt
        isDirty={isDisabled}
      />
    </Stack>
  )
}

export default JoinConversationSection